import { useState, MouseEvent, useMemo, useEffect } from 'react';
import { Box, IconButton, Menu, MenuItem } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import * as styles from './styles';
import { SortOption } from '@/types';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import {
  PointOfInterestTemplateTypeEnum,
  TemplateTypeEnum,
  usePaintBlockTemplateQuery,
  PointOfInterestTemplate,
  TemplateField,
  TemplateFieldTypeEnum,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { TOTAL_AREA_FIELD_FORMATTER } from '../../constants';
import useFetchTemplatesByType from '@/hooks/useFetchTemplatesByType';


type Props = {
  onFilterChange?: (checked: boolean) => void;
  onSortChange?: (sortBy: string | undefined, sortDir: number | undefined) => void;
};

const SORTING_ICON_MAP = new Map<undefined | 1 | -1, React.ReactNode>([
  [undefined, <SwapVertIcon />],
  [-1, <ArrowUpwardIcon />],
  [1, <ArrowDownwardIcon />],
]);


export const SavedBlockSortingHeader = ({ onSortChange }: Props) => {
  const structureId = useGetStructureId();

  const { data, loading, error } = usePaintBlockTemplateQuery({
    variables: {
      structureId,
      type: TemplateTypeEnum.PaintBlocks,
    },
    fetchPolicy: 'no-cache',
  });
  const {
    data: poiTemplatesData,
    loading: poiTemplatesLoading,
    error: poiTemplatesError,
  } = useFetchTemplatesByType();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [sortDir] = useState<undefined | 1 | -1>(-1);
  const open = Boolean(anchorEl);

  // Get POI template field keys mapping
  const poiTemplateFields = useMemo(() => {
    if (!poiTemplatesData?.allPointOfInterestTemplate) {
      return [];
    }

    const poiTemplates = poiTemplatesData.allPointOfInterestTemplate as PointOfInterestTemplate[];
    
    // Extract all POI template field keys
    return poiTemplates
      .filter(template => template.type === PointOfInterestTemplateTypeEnum.PaintBlockVolumeOfInterest)
      .map(template => ({
        templateId: template.id,
        templateName: template.name,
        fieldKeys: template.fields
          .filter((field: TemplateField) => field.key)
          .map((field: TemplateField) => ({
            key: field.key,
            name: field.name,
            type: field.type
          }))
      }));
    }, [poiTemplatesData?.allPointOfInterestTemplate]);

  // Create sort options from POI template fields that exist in paint region template
  const sortOptions: SortOption[] = useMemo(() => {
    const options: SortOption[] = [];

    // Get paint region template field keys for comparison
    const paintRegionFieldKeys = new Set(
      data?.allTemplates?.at(0)?.fields?.map((field) => field.key).filter(Boolean) || []
    );

    // Add POI template field keys only if they exist in paint region template
    poiTemplateFields.forEach(template => {
      template.fieldKeys.forEach((field: { key: string | null | undefined; name: string; type: TemplateFieldTypeEnum }) => {
        // Only include fields that exist in both POI and paint region templates
        if (field.key && paintRegionFieldKeys.has(field.key)) {
          options.push({
            id: field.key,
            label: `${field.name}`,
            type: 'poi' as const,
          });
        }
      });
    });

    return options;
  }, [poiTemplateFields, data?.allTemplates]);

  const [sortBy, setSortBy] = useState<string | undefined>(undefined);

  // Set default sortBy to the first valid mapped field after mapping
  useEffect(() => {
    if (sortOptions && sortOptions.length > 0) {
      // If current sortBy is not in the new options, or not set, set to first
      if (!sortBy || !sortOptions.some(opt => opt.id === sortBy)) {
        const defaultSortBy = sortOptions[0].id;
        setSortBy(defaultSortBy);
        // Call onSortChange to notify parent component about the default selection
        onSortChange?.(defaultSortBy, sortDir);
      }
    }
  }, [sortOptions, sortBy, sortDir, onSortChange]);

  const handleSortClick = (event: MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };



  const handleSortOptionSelect = (sortId: string): void => {
    setSortBy(sortId);
    setAnchorEl(null);
    onSortChange?.(sortId, sortDir);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  if (!sortOptions || sortOptions.length === 0) return <></>;

  const currentSortOption = sortOptions.find((option) => option.id === sortBy) || sortOptions[0];

  if (loading || poiTemplatesLoading) return <></>;

  if (error || poiTemplatesError)
    return <p>Error loading templates: {error?.message || poiTemplatesError?.message}</p>;

  return (
    <styles.FilterBar>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <IconButton size="small" disabled>
          {SORTING_ICON_MAP.get(sortDir)}
        </IconButton>
        <styles.SortButton
          sx={{ fontSize: '12px', fontWeight: 400 }}
          onClick={handleSortClick}
          endIcon={<KeyboardArrowDownIcon />}
          aria-controls={open ? 'sort-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          {TOTAL_AREA_FIELD_FORMATTER[currentSortOption?.label] || currentSortOption?.label}
        </styles.SortButton>

        <Menu
          id="sort-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'sort-button',
          }}
        >
          {sortOptions.map((option) => (
            <MenuItem
              key={option.id}
              onClick={() => handleSortOptionSelect(option.id)}
              selected={option.id === sortBy}
            >
              {TOTAL_AREA_FIELD_FORMATTER[option.label] || option.label}
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </styles.FilterBar>
  );
}; 